import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useEmailTestingService } from './email-testing.composable'
import type {
  InboxComposeBody,
  InboxComposeResponse,
  InboxConfig,
  InboxMessage,
  InboxWaitForBody
} from './email-testing.types'
import { getErrorMessage } from '@/utils/error-handler'

interface InboxFilters {
  to: string
  subject: string
  since: string | null
  /** Scope to a single merchant. Driven by the `?merchantId=X` URL query. */
  merchantId: number | null
  /** Scope to a single guest (customer). Driven by the `?customerId=X` URL query. */
  customerId: number | null
  /**
   * Free-text search — server-side OR'd substring across subject,
   * from, to, and textBody. Driven by the search input (debounced).
   */
  q: string
}

export const useEmailTestingStore = defineStore('adx-email-testing', () => {
  const service = useEmailTestingService()

  // List state
  const messages = ref<InboxMessage[]>([])
  const listLoading = ref(false)
  // Cursor returned by the last list call — null means we're on the
  // terminal page (or haven't fetched yet). Drives the Load more button.
  const nextCursor = ref<string | null>(null)
  const loadMoreLoading = ref(false)
  const filters = ref<InboxFilters>({ to: '', subject: '', since: null, merchantId: null, customerId: null, q: '' })

  // Detail state
  const detail = ref<InboxMessage | null>(null)
  const detailLoading = ref(false)

  // Clear-action loading is separate so the list keeps rendering while
  // the inbox is being cleared.
  const clearLoading = ref(false)

  // Compose-action loading for the compose / reply / forward flow.
  const composeLoading = ref(false)

  // Runtime config fetched once from the sink — powers the compose
  // modal's recipient-domain dropdown. Lazy-loaded and cached.
  const config = ref<InboxConfig | null>(null)
  async function loadConfig(): Promise<InboxConfig> {
    if (config.value) return config.value
    config.value = await service.config()
    return config.value
  }

  // Single error ref + hasError + clearError so `useStoreErrorWatch`
  // (the project convention) can surface failures from any of the
  // actions below.
  const error = ref<string | null>(null)
  const hasError = computed(() => error.value !== null)
  const hasMore = computed(() => nextCursor.value !== null)

  function clearError(): void {
    error.value = null
  }

  // Default page size. Callers that scan the full retained window (e.g. the
  // Template Coverage drain) pass a larger `limit`; the infinite-scroll list
  // view uses this default so scroll ticks stay small and snappy.
  const DEFAULT_PAGE_LIMIT = 200

  function buildParams(cursor?: string, limit: number = DEFAULT_PAGE_LIMIT): Record<string, unknown> {
    return {
      ...(filters.value.to ? { to: filters.value.to } : {}),
      ...(filters.value.since ? { since: filters.value.since } : {}),
      ...(filters.value.merchantId !== null ? { merchantId: filters.value.merchantId } : {}),
      ...(filters.value.customerId !== null ? { customerId: filters.value.customerId } : {}),
      ...(filters.value.q ? { q: filters.value.q } : {}),
      ...(cursor ? { cursor } : {}),
      limit
    }
  }

  async function fetchList(limit: number = DEFAULT_PAGE_LIMIT): Promise<void> {
    listLoading.value = true
    error.value = null
    try {
      // No recipient filter → sink returns the most recent messages across
      // every inbox. With a recipient filter, the (to.address, receivedAt)
      // compound index gives the fast path.
      const page = await service.listPage(buildParams(undefined, limit))
      messages.value = page.messages
      nextCursor.value = page.nextCursor
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to load inbox')
    } finally {
      listLoading.value = false
    }
  }

  /**
   * Append the next page to the existing list. No-op when `nextCursor`
   * is null (terminal page) or another load is already in flight.
   */
  async function loadMore(limit: number = DEFAULT_PAGE_LIMIT): Promise<void> {
    if (!nextCursor.value || loadMoreLoading.value) return
    loadMoreLoading.value = true
    error.value = null
    try {
      const page = await service.listPage(buildParams(nextCursor.value, limit))
      messages.value = [...messages.value, ...page.messages]
      nextCursor.value = page.nextCursor
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to load more messages')
    } finally {
      loadMoreLoading.value = false
    }
  }

  /**
   * Live-tail refresh — re-fetches the first page and prepends only the
   * messages not already loaded, WITHOUT discarding pages the user pulled in
   * via `loadMore()` or snapping their scroll position back to the top. Used
   * by the 5s live poll (and the /wait-for loop) so the inbox tails new
   * captures while infinite scroll holds the loaded history.
   *
   * Why not `fetchList()`: that replaces the whole list with page 1, which
   * with infinite scroll meant every poll tick wiped everything scrolled past
   * the first 100. `refreshHead()` is the merge-at-head variant.
   *
   * Dedupes by id. `nextCursor` is left untouched — it points at the loaded
   * tail so `loadMore()` keeps paging from where the user scrolled to. If
   * more than one page (`limit`) of new messages arrived since the last
   * refresh — implausible at a 5s cadence — only the newest page is merged;
   * a manual refresh / filter change (`fetchList`) reconciles the rest.
   * Failures are swallowed: a dropped poll tick just retries next interval.
   */
  async function refreshHead(): Promise<void> {
    try {
      const page = await service.listPage(buildParams())
      if (messages.value.length === 0) {
        // Nothing loaded yet (poll raced ahead of the initial fetch) — adopt
        // the page wholesale so `nextCursor` is correct for infinite scroll.
        messages.value = page.messages
        nextCursor.value = page.nextCursor
        return
      }
      const known = new Set(messages.value.map((m) => m.id))
      const fresh = page.messages.filter((m) => !known.has(m.id))
      if (fresh.length > 0) messages.value = [...fresh, ...messages.value]
    } catch {
      /* non-fatal — keep the current list and retry on the next tick */
    }
  }

  async function fetchDetail(id: string): Promise<void> {
    detailLoading.value = true
    error.value = null
    try {
      detail.value = await service.get(id)
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to load message')
    } finally {
      detailLoading.value = false
    }
  }

  /**
   * Returns the number of messages deleted on success.
   * Throws on failure so the caller can short-circuit its success path
   * (e.g. skip the success toast). The error is also captured into
   * `error` so `useStoreErrorWatch` will surface it.
   */
  async function clearInbox(): Promise<number> {
    if (!filters.value.to) return 0
    clearLoading.value = true
    error.value = null
    try {
      const deleted = await service.clear(filters.value.to)
      messages.value = []
      return deleted
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to clear inbox')
      throw err
    } finally {
      clearLoading.value = false
    }
  }

  /**
   * Injects a synthetic message via POST /messages and prepends it to the
   * current list (so the UI reflects the new message without a full refresh).
   * Throws on failure so the caller can keep the modal open; the error is
   * also captured into `error` for `useStoreErrorWatch` to surface.
   */
  async function composeMessage(body: InboxComposeBody): Promise<InboxComposeResponse> {
    composeLoading.value = true
    error.value = null
    try {
      const response = await service.compose(body)
      messages.value = [response.message, ...messages.value]
      return response
    } catch (err) {
      error.value = getErrorMessage(err, 'Failed to send message')
      throw err
    } finally {
      composeLoading.value = false
    }
  }

  /**
   * Long-poll wrapper around the sink's /wait-for endpoint. Returns the
   * matching message (or null on timeout). Intentionally doesn't mutate the
   * messages list — the caller is expected to call fetchList() after a hit
   * so the list view reflects whatever else the sink captured in the
   * meantime. Errors propagate to the caller rather than into `error` so the
   * live-tail loop can decide how to handle them without surfacing a toast.
   */
  async function waitForNext(body: InboxWaitForBody): Promise<InboxMessage | null> {
    return service.waitFor(body)
  }

  function reset(): void {
    messages.value = []
    detail.value = null
    nextCursor.value = null
    error.value = null
  }

  return {
    messages,
    listLoading,
    nextCursor,
    loadMoreLoading,
    hasMore,
    filters,
    detail,
    detailLoading,
    clearLoading,
    composeLoading,
    error,
    hasError,
    clearError,
    fetchList,
    loadMore,
    refreshHead,
    fetchDetail,
    clearInbox,
    composeMessage,
    waitForNext,
    config,
    loadConfig,
    reset,
    downloadAttachment: service.downloadAttachment
  }
})
